// Neu (SPEC 03 §7.2.1 Punkt 4; Vorlage docs/mockup/src.js `VerfahrenWahl`) — der Knopf in der
// Kopfleiste: Code-Chip, ab 1180 px der Titel, caret.
// Geändert (Rückmeldung Telefon 2026-09-30, „der Dropdown öffnet direkt ohne Selektion“): der
// Knopf öffnet ZUERST ein kleines Auswahlmenü wie die Kopf-Dropdowns in webgis/Projekte
// (Hausbaustein `Menue`: role="menu", Pfeiltasten, Esc, Tipp daneben schließt). Darin die
// Verfahren (Code mono · Titel · Kommune, das aktuelle mit Haken) — Tipp = wählen und zur
// Auswertung —, darunter „Alle Verfahren · Übersicht“ (das große Blatt) und für Admins
// „Verfahren anlegen“; für das aktuelle Verfahren „Ortsverzeichnis“ (E21). Am Telefon hängt das Menü über die volle Breite unter der Kopfleiste,
// Schrift ≥ 15 px, Zeilen ≥ 44 px (app.css `.vwahl-*` — nicht `vw-`, das ist die Verwaltung (ad-admin.css)).
// Geändert (Rückmeldung 01.10., „genug Platz“): 420 px breit, Titel und Kommune brechen um statt
// abgeschnitten zu werden; nur die Liste der Verfahren rollt (`.vwahl-liste`), die Aktionen darunter
// (Ortsverzeichnis, Alle Verfahren, Verfahren anlegen) bleiben auch bei vielen Verfahren sichtbar.
import { useCallback, useId, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { holeVerfahrenListe } from "../../api/client";
import { Icon, Menue } from "../ui";
import { useIch } from "../../hooks/useIch";
import { hatRecht, istAdmin } from "../../lib/rechte";
import { TX } from "../../lib/texte";
import { useStore } from "../../store/useStore";
import { sortiere } from "../../screens/verfahren/hilfen";

/** Breite des Menüs am Desktop (px); Menue begrenzt auf `100vw − 16 px` und schiebt es in den Schirm. */
export const VWAHL_BREITE = 420;
/** Höchstens 560 px, nie tiefer als der Schirm unter der Kopfleiste. */
export const VWAHL_MAX_HOEHE = "min(560px, calc(100dvh - var(--kopf-h) - var(--safe-top) - 24px))";

export function VerfahrenWahl({ V }: { V: string | null }) {
  const [offen, setOffen] = useState(false);
  const schliessen = useCallback(() => setOffen(false), []);
  const menueId = useId();
  const listenTitelId = useId();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: ich } = useIch();
  const oeffneBlatt = useStore((s) => s.setzeVerfahrenBlatt);
  const setzeVerfahren = useStore((s) => s.setzeVerfahren);
  const q = useQuery({ queryKey: ["verfahren"], queryFn: holeVerfahrenListe, staleTime: 60_000 });
  const liste = useMemo(() => sortiere(q.data?.eintraege ?? []), [q.data]);
  const titel = liste.find((v) => v.code === V)?.titel ?? "";

  const waehle = (code: string) => {
    setOffen(false);
    setzeVerfahren(code);
    navigate(`/v/${encodeURIComponent(code)}/auswerten`);
  };
  const alle = () => {
    setOffen(false);
    // Auf /verfahren IST die Übersicht schon die Seite — ein Blatt darüber sähe gleich aus und
    // ließe sich scheinbar nicht schließen.
    if (pathname !== "/verfahren") oeffneBlatt(true);
  };
  const anlegen = () => {
    setOffen(false);
    navigate("/admin/verfahren");
  };
  // E21 (05 §10): das Ortsverzeichnis des aktuellen Verfahrens (lesen; Bearbeiten mit Recht `orte`).
  const orte = () => {
    setOffen(false);
    if (V) navigate(`/v/${encodeURIComponent(V)}/orte`);
  };

  return (
    <div className="menue-anker vwahl-anker">
      <button
        type="button"
        className="verfahren-knopf ring-focus"
        aria-haspopup="menu"
        aria-expanded={offen}
        aria-controls={offen ? menueId : undefined}
        aria-label={V ? TX.shell.verfahrenWechseln(V, titel) : TX.shell.verfahrenWaehlen}
        title={V ? `${V} · ${titel}` : TX.shell.verfahrenWaehlen}
        // Der Knopf liegt AUSSERHALB des Menüs: ohne diese Zeile schlösse der mousedown des
        // Menüs („Klick daneben“) es, und der folgende click öffnete es sofort wieder.
        onMouseDown={(e) => e.stopPropagation()}
        onClick={() => setOffen((o) => !o)}
      >
        <span className="codemini" translate="no">
          {V ?? "—"}
        </span>
        {titel && <span className="vk-titel">{titel}</span>}
        <Icon name={offen ? "caret-up" : "caret-down"} size={13} />
      </button>
      <Menue
        open={offen}
        onClose={schliessen}
        width={VWAHL_BREITE}
        align="left"
        id={menueId}
        label={TX.shell.verfahrenWaehlen}
        // Das Menü selbst rollt nur als Notnagel (sehr niedriger Schirm); sonst rollt die Liste darin.
        style={{ maxHeight: VWAHL_MAX_HOEHE, overflowY: "auto", overscrollBehavior: "contain", padding: 8 }}
      >
        <div className="vwahl-titel" role="presentation" id={listenTitelId}>
          {TX.shell.verfahrenMenue}
        </div>
        <div className="vwahl-liste" role="group" aria-labelledby={listenTitelId}>
          {q.isPending && (
            <div className="vwahl-hinweis" role="presentation">
              {TX.allgemein.laedt}
            </div>
          )}
          {q.isError && (
            <div className="vwahl-hinweis" role="presentation">
              {TX.allgemein.laedtNicht}
            </div>
          )}
          {q.isSuccess && liste.length === 0 && (
            <div className="vwahl-hinweis" role="presentation">
              {TX.verfahren.keinZugriff}
            </div>
          )}
          {liste.map((v) => {
            const an = v.code === V;
            return (
              <button
                key={v.code}
                type="button"
                role="menuitemradio"
                aria-checked={an}
                className={`vwahl-punkt ring-focus${an ? " an" : ""}`}
                title={`${v.code} · ${v.titel}`}
                onClick={() => waehle(v.code)}
              >
                <span className="vwahl-code" translate="no">
                  {v.code}
                </span>
                <span className="vwahl-text">
                  <span className="vwahl-name">{v.titel}</span>
                  <span className="vwahl-kommune">{v.kommune}</span>
                </span>
                <Icon name="check" size={18} className="vwahl-haken" style={{ opacity: an ? 1 : 0 }} />
              </button>
            );
          })}
        </div>
        <hr />
        {V && hatRecht(ich, V, "lesen") && (
          <button type="button" role="menuitem" className="vwahl-punkt vwahl-aktion ring-focus" onClick={orte}>
            <Icon name="map-trifold" size={18} />
            <span className="vwahl-text">
              {TX.orte.menue} · {V}
            </span>
          </button>
        )}
        <button type="button" role="menuitem" className="vwahl-punkt vwahl-aktion ring-focus" onClick={alle}>
          <Icon name="stack" size={18} />
          <span className="vwahl-text">{TX.shell.alleVerfahren}</span>
        </button>
        {istAdmin(ich) && (
          <button type="button" role="menuitem" className="vwahl-punkt vwahl-aktion ring-focus" onClick={anlegen}>
            <Icon name="plus" size={18} />
            <span className="vwahl-text">{TX.verfahren.anlegen}</span>
          </button>
        )}
      </Menue>
    </div>
  );
}
